const percentile = (samples, fraction) => {
    if (samples.length === 0)
        return 0;
    const sorted = [...samples].sort((a, b) => a - b);
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction))];
};
export class RibbonPerfMonitor {
    /** The diagnostics overlay is on. Sampling itself is always on (it feeds the quality controller). */
    enabled;
    gl;
    timerExtension;
    overlay;
    frameIntervals = [];
    driverIntervals = [];
    driverTimestamp = null;
    qualityTransitions = [];
    submissions = 0;
    ticks = 0;
    cpuTimes = [];
    gpuTimes = [];
    pendingQueries = [];
    sampleEvery;
    gpuSampleCounter = 0;
    lastTimestamp = null;
    lastOverlayUpdate = 0;
    renderState = "starting";
    variant = "uncompiled";
    compilationMode = "synchronous";
    optimizationMode;
    coverage = "full";
    cadence = "full";
    quality = "full";
    cssSize = [1, 1];
    bufferSize = [1, 1];
    constructor(gl, optimizationMode, diagnostics) {
        this.gl = gl;
        this.optimizationMode = optimizationMode;
        this.enabled = diagnostics?.get("auroraPerf") === "1";
        // One GPU sample every 12 submitted frames by default — enough for the
        // quality controller to decide within ~3 s at 30 fps, sparse enough not to
        // perturb pacing on tile-based GPUs. `auroraPerfSample=1` samples every
        // frame for the automated harness.
        this.sampleEvery = Math.max(1, Number(diagnostics?.get("auroraPerfSample") ?? 12) || 12);
        // Always requested: the adaptive quality controller reads GPU time even
        // when nobody is looking at the overlay.
        this.timerExtension = gl.getExtension("EXT_disjoint_timer_query_webgl2");
        if (!this.enabled || typeof document === "undefined") {
            this.overlay = null;
            return;
        }
        const overlay = document.createElement("output");
        overlay.setAttribute("aria-label", "Ribbon performance diagnostics");
        overlay.setAttribute("data-ribbon-perf", "");
        Object.assign(overlay.style, {
            position: "fixed",
            right: "12px",
            bottom: "12px",
            zIndex: "2147483647",
            width: "min(360px, calc(100vw - 24px))",
            padding: "10px 12px",
            borderRadius: "8px",
            background: "rgba(14, 21, 27, 0.9)",
            color: "#e8f5ff",
            font: "11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace",
            whiteSpace: "pre-wrap",
            pointerEvents: "none",
        });
        document.body.appendChild(overlay);
        this.overlay = overlay;
        this.paint(performance.now());
    }
    /** Raw samples for an automated harness (`window.__ribbonPerf`). */
    snapshot() {
        return {
            frameIntervals: [...this.frameIntervals],
            driverIntervals: [...this.driverIntervals],
            submissions: this.submissions,
            ticks: this.ticks,
            qualityTransitions: [...this.qualityTransitions],
            gpuTimingAvailable: this.hasGpuTiming,
            dutyIsEstimate: true,
            cpuTimes: [...this.cpuTimes],
            gpuTimes: [...this.gpuTimes],
            variant: this.variant,
            coverage: this.coverage,
            cadence: this.cadence,
            quality: this.quality,
            duty: this.gpuDuty(),
            renderState: this.renderState,
            cssSize: this.cssSize,
            bufferSize: this.bufferSize,
        };
    }
    reset() {
        // Samples submitted before a scene/visibility/tier change belong to the
        // previous measurement window, even if the driver delivers them later.
        this.pendingQueries.forEach((query) => this.gl.deleteQuery(query));
        this.pendingQueries.length = 0;
        this.gpuSampleCounter = 0;
        this.driverIntervals.length = 0;
        this.driverTimestamp = null;
        this.submissions = 0;
        this.ticks = 0;
        this.frameIntervals.length = 0;
        this.cpuTimes.length = 0;
        this.gpuTimes.length = 0;
        this.lastTimestamp = null;
    }
    setRenderState(state) {
        if (!this.enabled || this.renderState === state)
            return;
        this.renderState = state;
        this.paint(performance.now(), true);
    }
    setFrameInfo(variant, cssSize, bufferSize, coverage = "full") {
        this.variant = variant;
        this.cssSize = cssSize;
        this.bufferSize = bufferSize;
        this.coverage = coverage;
    }
    setCadence(cadence) {
        this.cadence = cadence;
    }
    setCompilationMode(mode) {
        if (!this.enabled)
            return;
        this.compilationMode = mode;
    }
    beginGpuQuery() {
        this.gpuSampleCounter += 1;
        if (!this.timerExtension ||
            this.gpuSampleCounter % this.sampleEvery !== 0 ||
            this.pendingQueries.length >= 4)
            return null;
        const query = this.gl.createQuery();
        if (!query)
            return null;
        this.gl.beginQuery(this.timerExtension.TIME_ELAPSED_EXT, query);
        return query;
    }
    endGpuQuery(query) {
        if (!query || !this.timerExtension)
            return;
        this.gl.endQuery(this.timerExtension.TIME_ELAPSED_EXT);
        this.pendingQueries.push(query);
    }
    recordFrame(timestamp, cpuTimeMs) {
        if (this.lastTimestamp !== null) {
            this.pushSample(this.frameIntervals, timestamp - this.lastTimestamp);
        }
        this.lastTimestamp = timestamp;
        this.submissions += 1;
        this.pushSample(this.cpuTimes, cpuTimeMs);
        this.pollGpuQueries();
        if (this.enabled)
            this.paint(timestamp);
    }
    /** Whether GPU timing is available at all (no timer queries on Safari). */
    get hasGpuTiming() {
        return this.timerExtension !== null;
    }
    setQuality(tier) {
        this.quality = tier;
        this.qualityTransitions.push({ tier, at: performance.now() });
        if (this.qualityTransitions.length > 100)
            this.qualityTransitions.shift();
    }
    recordTick(timestamp) {
        if (this.driverTimestamp !== null && timestamp > this.driverTimestamp) {
            this.pushSample(this.driverIntervals, timestamp - this.driverTimestamp);
        }
        this.driverTimestamp = timestamp;
        this.ticks += 1;
    }
    resetFrameClock() {
        this.lastTimestamp = null;
        this.driverTimestamp = null;
    }
    /**
     * GPU duty: fraction of wall time the GPU spends on this canvas, from the
     * GPU p95 per submitted frame and the recent submitted-frame rate. Null
     * until enough samples exist (or without timer queries).
     */
    gpuDuty() {
        const p95 = this.gpuP95();
        if (p95 === null || this.frameIntervals.length < 8)
            return null;
        // (gpuP95 needs at least four samples; see below.)
        const recent = this.frameIntervals.slice(-60);
        const meanInterval = recent.reduce((sum, v) => sum + v, 0) / recent.length;
        if (!(meanInterval > 0))
            return null;
        return Math.min(p95 / meanInterval, 4);
    }
    /** Latest GPU p95 in ms, or null when no samples exist yet. */
    gpuP95() {
        if (!this.timerExtension)
            return null;
        this.pollGpuQueries();
        return this.gpuTimes.length >= 4 ? percentile(this.gpuTimes, 0.95) : null;
    }
    destroy() {
        this.pendingQueries.forEach((query) => this.gl.deleteQuery(query));
        this.pendingQueries.length = 0;
        this.overlay?.remove();
    }
    pushSample(target, value) {
        target.push(value);
        if (target.length > 600)
            target.shift();
    }
    pollGpuQueries() {
        if (!this.timerExtension)
            return;
        while (this.pendingQueries.length > 0) {
            const query = this.pendingQueries[0];
            const ready = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE);
            if (!ready)
                break;
            this.pendingQueries.shift();
            const disjoint = this.gl.getParameter(this.timerExtension.GPU_DISJOINT_EXT);
            if (!disjoint) {
                const nanoseconds = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT);
                this.pushSample(this.gpuTimes, nanoseconds / 1_000_000);
            }
            this.gl.deleteQuery(query);
        }
    }
    paint(now, force = false) {
        if (!this.overlay || (!force && now - this.lastOverlayUpdate < 500))
            return;
        this.lastOverlayUpdate = now;
        const frameP50 = percentile(this.frameIntervals, 0.5);
        const frameP95 = percentile(this.frameIntervals, 0.95);
        const frameP99 = percentile(this.frameIntervals, 0.99);
        const dropped = this.frameIntervals.filter((sample) => sample > 33.3).length;
        const dpr = this.cssSize[0] > 0 ? this.bufferSize[0] / this.cssSize[0] : 0;
        this.overlay.value = [
            `Ribbon: ${this.renderState}`,
            `variant: ${this.variant}`,
            `mode: ${this.optimizationMode}`,
            `coverage: ${this.coverage}`,
            `cadence: ${this.cadence}`,
            `quality: ${this.quality}${this.gpuDuty() !== null ? ` · estimated duty ${(this.gpuDuty() * 100).toFixed(0)}%` : ""}`,
            `idle compile: ${this.compilationMode}`,
            `css: ${this.cssSize[0]} x ${this.cssSize[1]}`,
            `buffer: ${this.bufferSize[0]} x ${this.bufferSize[1]} @ ${dpr.toFixed(2)}x`,
            `fps(p50): ${frameP50 > 0 ? (1000 / frameP50).toFixed(1) : "--"}`,
            `frame p50/p95/p99: ${frameP50.toFixed(2)} / ${frameP95.toFixed(2)} / ${frameP99.toFixed(2)} ms`,
            `frames >33.3ms: ${dropped}/${this.frameIntervals.length}`,
            `cpu submit p95: ${percentile(this.cpuTimes, 0.95).toFixed(2)} ms`,
            `gpu p95: ${this.gpuTimes.length ? `${percentile(this.gpuTimes, 0.95).toFixed(2)} ms` : "unavailable"}`,
        ].join("\n");
    }
}
//# sourceMappingURL=perf-monitor.js.map